import { Getter, Setter, atom } from "jotai";
import { atomWithLocation } from "jotai-location";

const locationAtom = atomWithLocation();

type Action<T> = T | ((prev: T) => T);

type WithDefaultValue<T> = {
  defaultValue: T;
  getDefaultValue?: never;
};

type WithGetDefaultValue<T> = {
  getDefaultValue: (get: Getter) => T;
  defaultValue?: never;
};

type SearchParamsAtomArgs<T> = {
  key: string;
  parse?: (value: string) => T;
  serialize?: (value: T) => string;
  sideEffectOnSet?: (
    get: Getter,
    set: Setter,
    searchParams: URLSearchParams,
  ) => void;
} & (WithDefaultValue<T> | WithGetDefaultValue<T>);

export const searchParamAtom = <T>({
  key,
  defaultValue,
  getDefaultValue,
  parse = (value) => value as T,
  serialize = (value) => value as string,
  sideEffectOnSet,
}: SearchParamsAtomArgs<T>) =>
  atom(
    (get) => {
      const searchParams = get(locationAtom).searchParams;
      const fallbackValue = getDefaultValue
        ? getDefaultValue(get)
        : defaultValue;

      const searchParamValue =
        searchParams?.get(key) ?? serialize(fallbackValue);
      return parse(searchParamValue);
    },
    (get, set, action: Action<T>) => {
      const location = get(locationAtom);
      const searchParams = new URLSearchParams(location.searchParams);

      const args = {
        key,
        parse,
        serialize,
      } as SearchParamsAtomArgs<T>;

      if (getDefaultValue) {
        (args as WithGetDefaultValue<T>).getDefaultValue = getDefaultValue;
      } else {
        (args as WithDefaultValue<T>).defaultValue = defaultValue as T;
      }

      const currentValue = get(searchParamAtom(args));

      const newValue =
        typeof action === "function"
          ? (action as (prev: T) => T)(currentValue)
          : action;

      const serializedNewValue = serialize(newValue);

      if (serializedNewValue) {
        searchParams.set(key, serialize(newValue));
      } else {
        searchParams.delete(key);
      }

      if (sideEffectOnSet) {
        sideEffectOnSet(get, set, searchParams);
      }

      set(locationAtom, { ...location, searchParams });
    },
  );
